import { useState, useEffect, useRef } from "react";
import { PhoneCall, UserPlus, Calendar, CalendarCheck, Bot, User } from "lucide-react";

const features = [
  {
    icon: PhoneCall,
    title: "Answers Calls & Chats Instantly",
    description: "Picks up on the first ring, 24/7.",
  },
  {
    icon: UserPlus,
    title: "Captures Leads Automatically",
    description: "Collects name, phone and service.",
  },
  {
    icon: Calendar,
    title: "Checks Real-Time Availability",
    description: "Connected to your calendar for live slots.",
  },
  {
    icon: CalendarCheck,
    title: "Books Appointments Automatically",
    description: "Confirms bookings on the spot.",
  },
];

const conversation = [
  {
    role: "customer",
    text: "Hi, I'd like to book a haircut tomorrow.",
  },
  {
    role: "ai",
    parts: [
      { type: "text", content: "Sure! We have available appointments at " },
      { type: "bold", content: "2:30 PM" },
      { type: "text", content: " and " },
      { type: "bold", content: "4:00 PM" },
      { type: "text", content: " tomorrow. Which time works best for you?" },
    ],
  },
  {
    role: "customer",
    text: "2:30 PM please.",
  },
  {
    role: "ai",
    parts: [
      { type: "text", content: "Perfect! Your appointment is confirmed for " },
      { type: "bold", content: "2:30 PM tomorrow" },
      { type: "text", content: ". Can I have your name and phone number?" },
    ],
  },
  {
    role: "customer",
    text: "Anna, +1 555-239-1123.",
  },
  {
    role: "ai",
    parts: [
      { type: "text", content: "Thank you, Anna! Your appointment is booked. We look forward to seeing you!" },
    ],
    badge: "Appointment Confirmed",
  },
];

function RenderParts({ parts }) {
  return (
    <>
      {parts.map((part, i) =>
        part.type === "bold" ? (
          <span key={i} className="font-semibold text-[#0F172A]">{part.content}</span>
        ) : (
          <span key={i}>{part.content}</span>
        )
      )}
    </>
  );
}

function ChatDemo() {
  const [visibleCount, setVisibleCount] = useState(0);
  const [started, setStarted] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.4 }
    );
    const el = chatEndRef.current?.parentElement;
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    if (visibleCount >= conversation.length) return;
    const delay = visibleCount === 0 ? 600 : conversation[visibleCount - 1]?.role === "ai" ? 1400 : 1000;
    const timer = setTimeout(() => setVisibleCount((c) => c + 1), delay);
    return () => clearTimeout(timer);
  }, [started, visibleCount]);

  useEffect(() => {
    if (visibleCount > 0 && chatEndRef.current) {
      const container = chatEndRef.current.parentElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [visibleCount]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden flex flex-col flex-1">
      {/* Chat header */}
      <div className="bg-[#0F172A] px-5 py-3.5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-sm font-medium text-white">AI Receptionist</p>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs text-slate-400">Online</span>
          </div>
        </div>
      </div>

      {/* Chat body */}
      <div className="p-4 md:p-5 flex-1 overflow-y-auto flex flex-col gap-3 bg-[#F8FAFC]" data-testid="chat-demo-body">
        {/* Timestamp */}
        <div className="text-center">
          <span className="text-[10px] text-slate-400 bg-white px-2.5 py-0.5 rounded-full border border-slate-100">
            Today, 10:14 AM
          </span>
        </div>

        {conversation.slice(0, visibleCount).map((msg, index) => (
          <div
            key={index}
            className={`flex ${msg.role === "ai" ? "justify-start" : "justify-end"} animate-fade-in-up`}
            style={{ animationDuration: "0.3s" }}
          >
            <div className={`flex items-end gap-2 max-w-[85%] ${msg.role === "ai" ? "" : "flex-row-reverse"}`}>
              {/* Avatar */}
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                msg.role === "ai" ? "bg-[#3B82F6]" : "bg-slate-300"
              }`}>
                {msg.role === "ai" ? (
                  <Bot className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
                ) : (
                  <User className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
                )}
              </div>

              {/* Bubble */}
              <div className={`rounded-2xl px-4 py-2.5 ${
                msg.role === "ai"
                  ? "bg-white border border-slate-200 rounded-bl-md"
                  : "bg-[#0F172A] text-white rounded-br-md"
              }`}>
                <p className={`text-sm leading-relaxed ${msg.role === "ai" ? "text-slate-600" : "text-white"}`}>
                  {msg.text ? msg.text : <RenderParts parts={msg.parts} />}
                </p>
                {msg.badge && (
                  <div className="mt-2 inline-flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span className="text-xs font-medium text-green-700">{msg.badge}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {visibleCount < conversation.length && started && visibleCount > 0 && (
          <div className="flex justify-start animate-fade-in">
            <div className="flex items-end gap-2">
              <div className="w-7 h-7 rounded-full bg-[#3B82F6] flex items-center justify-center shrink-0">
                <Bot className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>
    </div>
  );
}

export default function SolutionSection() {
  return (
    <section id="solution" data-testid="solution-section" className="py-10 md:py-[85px] bg-white">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-12 items-stretch">
          {/* Left: Chat demo */}
          <div className="order-2 lg:order-1 flex" data-testid="solution-chat-demo">
            <div className="w-full flex flex-col">
              <ChatDemo />
            </div>
          </div>

          {/* Right: Copy + Feature cards */}
          <div className="order-1 lg:order-2 flex flex-col justify-center">
            <span className="inline-block text-xs font-medium uppercase tracking-widest text-[#3B82F6] mb-4">
              The Solution
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0F172A] tracking-tight text-balance mb-4">
              Your Always-On{" "}
              <span className="text-[#3B82F6]">AI Receptionist</span>
            </h2>
            <p className="text-base md:text-[17px] text-slate-600 leading-relaxed mb-10 max-w-lg">
              AICallFront answers calls and chats like a trained receptionist — capturing leads, checking your calendar, and booking appointments around the clock.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={feature.title}
                    className="bg-[#F8FAFC] border border-slate-100 rounded-xl p-4 hover:border-blue-200 hover:shadow-md transition-all duration-300 group"
                    data-testid={`solution-feature-${index}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center mb-3 group-hover:bg-[#3B82F6]/15 transition-colors">
                      <Icon className="w-4 h-4 text-[#3B82F6]" strokeWidth={1.5} />
                    </div>
                    <h4 className="font-heading font-semibold text-sm text-[#0F172A] mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
